import { ColumnMetadata } from './columnMetadata';
import { ColumnMetadataOriginItem } from './columnMetadataOriginItem';
import { CustomFormat } from './customFormat';
import { Encodings } from './encodings';
import { Queries } from './queries';
import { Schemas } from './schemas';
import { ToolScope } from './toolScope';
import { UniqueKeys } from './uniqueKeys';
// NOT SERIALIZABLE
export class ToolScopeBuilder {
    constructor(...ins) {
        this.data = new Map();
        this.schemas = new Map();
        this.samples = new Map();
        this.encodings = new Map();
        this.uniqueKeys = new Map();
        this.exports = new Map();
        const merged = ToolScope.merge(...ins);
        if (merged.user) {
            this.user = merged.user;
        }
        this.pipeID = merged.pipeID;
        this.dependants = merged.dependants;
        this.dependee = merged.dependee;
        this.dependeeMeta = merged.dependeeMeta;
        this.reusableLogicID = merged.reusableLogicID;
        this.reusableLogicDependants = merged.reusableLogicDependants;
        this.reusableLogic = merged.reusableLogic;
        this.reusableLogicMeta = merged.reusableLogicMeta;
        this.ins = merged;
    }
    build(emptySamples) {
        return new ToolScope(this.user, new Schemas(this.data), // TODO: this doesn't have to be a schemas, can just be data?
        new Schemas(this.schemas), emptySamples ? Queries.empty : new Queries(this.samples), new Encodings(this.encodings), new UniqueKeys(this.uniqueKeys), this.pipeID, this.dependants, this.dependee, this.dependeeMeta, this.reusableLogicID, this.reusableLogicDependants, this.reusableLogic, this.reusableLogicMeta);
    }
    setPipeID(pipeID) {
        this.pipeID = pipeID;
        return this;
    }
    setUser(user) {
        this.user = user;
        return this;
    }
    setData(fileID, columns) {
        const columnMetadata = [];
        columns.forEach((col) => {
            columnMetadata.push(ColumnMetadata.fromFileColumn(fileID, col));
        });
        this.data.set(fileID, columnMetadata);
        return this;
    }
    // used by tests
    setSchema(exportName, columns, fileID = 'aAaAAaaAaAaA', customFormats) {
        const colMet = [];
        columns.forEach((col) => {
            const colOrigins = new Map();
            const colOrigin = new ColumnMetadataOriginItem([col]);
            const format = customFormats?.get(col.name);
            colOrigins.set(fileID, colOrigin);
            colMet.push(new ColumnMetadata(col, colOrigins, false, format));
        });
        this.schemas.set(exportName, colMet);
        return this;
    }
    appendSchema(exportName, column) {
        let schema = this.schemas.get(exportName);
        if (!schema) {
            schema = [];
            this.schemas.set(exportName, schema);
        }
        schema.push(column);
        return this;
    }
    setSample(exportName, query) {
        this.samples.set(exportName, query);
        return this;
    }
    setDependee(dependee, dependeeMeta) {
        this.dependee = dependee;
        this.dependeeMeta = dependeeMeta;
        return this;
    }
    setDependants(dependants) {
        this.dependants = dependants;
        return this;
    }
    setUniqueKeys(exportName, uniqueKeys) {
        this.uniqueKeys.set(exportName, uniqueKeys);
        return this;
    }
    setReusableLogicID(reusableLogicID) {
        this.reusableLogicID = reusableLogicID;
        return this;
    }
    setReusableLogicDependants(reusableLogicDependants) {
        this.reusableLogicDependants = reusableLogicDependants;
        return this;
    }
    setReusableLogic(reusableLogic, reusableLogicMeta) {
        this.reusableLogic = reusableLogic;
        this.reusableLogicMeta = reusableLogicMeta;
        return this;
    }
    export(exportName) {
        if (this.exports.has(exportName))
            throw new Error(`Export name ${exportName} already exists`);
        const accessor = new ExportAccessor(exportName, this.schemas, this.samples, this.encodings, this.uniqueKeys);
        const ex = new Exports(this.ins, accessor);
        this.exports.set(exportName, ex);
        return ex;
    }
    exportAll() {
        // exports every name from in
        for (const name of this.ins.names) {
            this.export(name).copyAll(name);
        }
        return this;
    }
    has(exportName) {
        return this.schemas.has(exportName) || this.samples.has(exportName) || this.encodings.has(exportName);
    }
}
export class ExportAccessor {
    constructor(exportName, schemas, samples, encodings, uniqueKeys) {
        this.exportName = exportName;
        this.schemas = schemas;
        this.samples = samples;
        this.encodings = encodings;
        this.uniqueKeys = uniqueKeys;
    }
    get schema() {
        return this.schemas.get(this.exportName);
    }
    set schema(columns) {
        if (columns) {
            this.schemas.set(this.exportName, columns);
        }
        else {
            this.schemas.delete(this.exportName);
        }
    }
    get sample() {
        return this.samples.get(this.exportName);
    }
    set sample(sample) {
        if (sample) {
            this.samples.set(this.exportName, sample);
        }
        else {
            this.samples.delete(this.exportName);
        }
    }
    get encoding() {
        return this.encodings.get(this.exportName);
    }
    set encoding(encoding) {
        if (encoding) {
            this.encodings.set(this.exportName, encoding);
        }
        else {
            this.encodings.delete(this.exportName);
        }
    }
    get uniqueKey() {
        return this.uniqueKeys.get(this.exportName);
    }
    set uniqueKey(uniqueKeys) {
        if (uniqueKeys) {
            this.uniqueKeys.set(this.exportName, uniqueKeys);
        }
        else {
            this.uniqueKeys.delete(this.exportName);
        }
    }
}
export class Exports {
    constructor(ins, accessor) {
        this.ins = ins;
        this.accessor = accessor;
    }
    // should this always be required?
    makeNew(column, dependents = [], required) {
        const newCol = ColumnMetadata.fromParents(column, dependents, required).toNewColumnMetadata(undefined, undefined, undefined, undefined, undefined, undefined, true);
        return this.addColumn(newCol, false);
    }
    changeType(source, srcColumn, newType, required, alias) {
        const column = srcColumn.toNewColumnMetadata(required, alias, newType, undefined, undefined, true);
        const encoded = this.isEncoded(source, srcColumn.name);
        return this.addColumn(column, encoded);
    }
    copy(source, columnMetadata, changeValues, required, dependents, // sometimes you want to add some more dependees to this item
    alias) {
        if (columnMetadata) {
            const encoded = changeValues ? false : this.isEncoded(source, columnMetadata.name);
            const copy = columnMetadata.toNewColumnMetadata(required, alias, undefined, undefined, undefined, changeValues || alias !== undefined);
            if (dependents)
                copy.addNewColumnOrigins(dependents);
            return this.addColumn(copy, encoded);
        }
        return this;
    }
    encode(column, required) {
        return this.addColumn(column.toNewColumnMetadata(required), true);
    }
    isEncoded(source, column) {
        const encoding = this.ins.encodings.at(source);
        if (!encoding) {
            return false;
        }
        return encoding.some((e) => e === column);
    }
    findSourceColumn(source, column) {
        const schema = this.ins.schemas.at(source);
        if (!schema) {
            return undefined;
        }
        return schema.find((c) => c.name === column);
    }
    findOutColumn(column) {
        const schema = this.accessor.schema;
        if (!schema) {
            return undefined;
        }
        return schema.find((c) => c.name === column);
    }
    getAllColumnMetadata() {
        return this.ins.schemas.columnMetadata();
    }
    addColumn(column, encoded) {
        let schema = this.accessor.schema;
        if (!schema) {
            schema = [];
            this.accessor.schema = schema;
        }
        schema.push(column);
        if (encoded) {
            let encoding = this.accessor.encoding;
            if (!encoding) {
                encoding = [];
                this.accessor.encoding = encoding;
            }
            encoding.push(column.name);
        }
        return this;
    }
    copyAll(source) {
        // copies a source in full all aspects
        if (this.accessor.schema) {
            throw new Error(`Cannot copy over an existing name. Use append(...column...) to piece together new output.`);
        }
        const schema = this.ins.schemas.at(source);
        if (schema) {
            this.accessor.schema = this.initializeChangedColumns(schema);
            const sample = this.ins.samples.squel(source);
            if (sample) {
                this.accessor.sample = sample;
            }
        }
        const encoding = this.ins.encodings.at(source);
        if (encoding) {
            this.accessor.encoding = encoding.slice();
        }
        return this;
    }
    data(fileID) {
        const columns = this.ins.data.at(fileID);
        if (columns) {
            this.accessor.schema = columns.slice();
        }
        return this;
    }
    setColumnFormats(customFormats) {
        if (!customFormats) {
            return this;
        }
        const columns = this.accessor.schema;
        const newSchema = columns?.map((col) => {
            if (customFormats.has(col.name)) {
                const format = customFormats.get(col.name);
                if (format) {
                    return col.toNewColumnMetadata(undefined, undefined, undefined, false, format, col.changed, col.isNew);
                }
                return col.toNewColumnMetadata(undefined, undefined, undefined, true, undefined, col.changed, col.isNew);
            }
            return col;
        });
        this.accessor.schema = newSchema;
        return this;
    }
    initializeColumnFormats() {
        const columns = this.accessor.schema;
        const newSchema = columns?.map((col) => {
            if (col.customFormat) {
                if (!col.customFormat.inherited) {
                    return col.toNewColumnMetadata(undefined, undefined, undefined, false, col.customFormat.copyWithInheritedTrue(), col.changed, col.isNew);
                }
            }
            else if (col.type === 'number' || col.type === 'date') {
                return col.toNewColumnMetadata(undefined, undefined, undefined, false, CustomFormat.defaultFormat(col.type === 'number'), col.changed, col.isNew);
            }
            return col;
        });
        this.accessor.schema = newSchema;
        return this;
    }
    initializeChangedColumns(columns) {
        const newSchema = columns.map((col) => col.toNewColumnMetadata(undefined, undefined, undefined, false, col.customFormat, false, false));
        return newSchema;
    }
    setUniqueKeys(keys) {
        this.accessor.uniqueKey = keys;
        return this;
    }
}
//# sourceMappingURL=toolScopeBuilder.js.map